// Client layer (TS-2.5.03) - BRD section 8.6, decisions.md D-004/D-008.
//
// THE LIST-CODE PICKER IS A CLOSED SELECT, AND IT IS THE SERVER'S LIST. D-233 recorded this field
// as free text over a hand-maintained `KNOWN_LIST_CODES` array, because nothing enumerated the 13
// vocabularies D-004 names and guessing the six codes this codebase had never called would have
// shipped literals nobody could verify. `GET /api/config/reference-lists` now answers with every
// vocabulary's code, display name and value counts, so the picker shows what this deployment
// actually holds - including any list an administrator added after release, which a hardcoded
// array could never have done.
//
// THE CATALOGUE LOADS FIRST AND THE VALUES FOLLOW. The two reads are deliberately not merged: the
// catalogue is fetched once and carries no values, while the list read re-runs on every code
// change and on every `includeInactive` toggle. A single combined read would refetch thirteen
// vocabularies' worth of metadata every time an admin ticked a checkbox.
//
// A CATALOGUE THAT FAILS DOES NOT TAKE THE PANEL WITH IT. The selected code falls back to the
// default the panel has always opened on, so a screen whose catalogue read failed still edits the
// team list rather than showing an empty picker over an empty table.
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../api/ApiError";
import type {
  ReferenceListDetail,
  ReferenceListSummary,
  ReferenceValue,
  RetirementImpact,
} from "../../api/generated/contracts";
import { configurationApi } from "../../api/configuration";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { WarningsBanner } from "../../components/WarningsBanner";
import { issueBannerItems } from "./lib/adminBanners";
import { draftFromReferenceValue, EMPTY_REFERENCE_VALUE_DRAFT, referenceValueFromDraft } from "./lib/referenceValueDraft";
import type { ReferenceValueDraft } from "./lib/referenceValueDraft";
import { ReferenceValueForm } from "./ReferenceValueForm";
import { strings } from "../../strings";

// The vocabulary this panel opens on when the catalogue has not answered yet, and the one it falls
// back to if the catalogue read fails. "team" is the list UC-17's worked example edits (D-008's
// "add teams beyond the current seven"), and it is seeded in every deployment.
const DEFAULT_LIST_CODE = "team";

const F = strings.admin.referenceData;

export function ReferenceDataPanel() {
  const [catalogue, setCatalogue] = useState<ReferenceListSummary[]>([]);
  const [listCode, setListCode] = useState(DEFAULT_LIST_CODE);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [detail, setDetail] = useState<ReferenceListDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [addDraft, setAddDraft] = useState<ReferenceValueDraft>(EMPTY_REFERENCE_VALUE_DRAFT);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<ReferenceValueDraft>(EMPTY_REFERENCE_VALUE_DRAFT);
  const [saving, setSaving] = useState(false);
  const [banners, setBanners] = useState<ReturnType<typeof issueBannerItems>>([]);

  const [retiringKey, setRetiringKey] = useState<string | null>(null);
  const [retirementImpact, setRetirementImpact] = useState<RetirementImpact | null>(null);
  const [assessingImpact, setAssessingImpact] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    void configurationApi
      .getReferenceList(listCode, includeInactive)
      .then((next) => {
        setDetail(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The reference list could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [listCode, includeInactive]);

  useEffect(refresh, [refresh]);

  // Fetched once. A catalogue failure is deliberately not surfaced as the panel's error: the list
  // read below owns that banner, and a picker that could not populate is a degraded control rather
  // than a broken screen.
  useEffect(() => {
    let cancelled = false;
    void configurationApi
      .listReferenceLists()
      .then((next) => {
        if (cancelled) return;
        setCatalogue(next);
        if (next.length > 0 && !next.some((summary) => summary.listCode === DEFAULT_LIST_CODE)) {
          setListCode(next[0]!.listCode);
        }
      })
      .catch(() => {
        if (!cancelled) setCatalogue([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function saveValue(value: ReferenceValue) {
    setSaving(true);
    try {
      // ReferenceValue carries no concurrencyToken of its own (unlike ProjectRecord/NoteRecord) -
      // ReferenceValueSaveRequest.concurrencyToken exists for a future optimistic-concurrency path
      // this DTO does not yet surface a value to round-trip for, so null is the only honest value here.
      const result = await configurationApi.saveReferenceValue({ value, concurrencyToken: null });
      setBanners(issueBannerItems(result.issues));
      if (result.saved) {
        setShowAdd(false);
        setEditingKey(null);
        refresh();
      }
    } finally {
      setSaving(false);
    }
  }

  async function startRetire(value: ReferenceValue) {
    setRetiringKey(value.key);
    setAssessingImpact(true);
    setRetirementImpact(null);
    try {
      const impact = await configurationApi.assessRetirement(value.key);
      setRetirementImpact(impact);
    } finally {
      setAssessingImpact(false);
    }
  }

  async function confirmRetire(value: ReferenceValue) {
    setRetiringKey(null);
    await saveValue({ ...value, isActive: false });
  }

  async function reactivate(value: ReferenceValue) {
    await saveValue({ ...value, isActive: true });
  }

  // The counts come from the catalogue rather than from the rows on screen, for
  // ReferenceListDetail's own reason: "active" is a property of the row, and a surface that
  // filtered for itself would re-derive the retirement rule every time it rendered. They are also
  // the one thing the table cannot show when includeInactive is off - a list with three retired
  // values looks identical to one with none.
  const selected = catalogue.find((summary) => summary.listCode === listCode) ?? null;

  // Only lists that carry colors get a color column (ReferenceList.allowsColor); a "team"-style
  // list without one keeps its four columns rather than showing a column of dashes.
  const showColorColumn = detail?.list.allowsColor ?? false;
  const columnCount = showColorColumn ? 5 : 4;

  return (
    <section className="eg-admin-reference-data">
      <h2>{F.heading}</h2>

      <div className="eg-admin__toolbar">
        <FormField id="eg-admin-list-code" label={F.listPicker}>
          <select
            value={listCode}
            onChange={(event) => setListCode(event.target.value)}
            {...formFieldControlProps("eg-admin-list-code")}
          >
            {catalogue.length === 0 && <option value={listCode}>{listCode}</option>}
            {catalogue.map((summary) => (
              <option key={summary.listCode} value={summary.listCode}>
                {summary.displayName} ({summary.listCode})
              </option>
            ))}
          </select>
        </FormField>

        <label>
          <input type="checkbox" checked={includeInactive} onChange={(event) => setIncludeInactive(event.target.checked)} />
          {F.includeInactive}
        </label>

        {selected && (
          <p className="eg-admin-reference-data__counts">
            {selected.activeValueCount} {F.activeValueCount}
            {selected.retiredValueCount > 0 ? `, ${selected.retiredValueCount} ${F.retiredValueCount}` : ""}
          </p>
        )}

        {/* The one "Add value" action is anchored here in the toolbar rather than floating loose
            below the table; the add form itself still opens beneath the table. */}
        {!showAdd && (
          <div className="eg-admin__toolbar-actions">
            <button type="button" className="eg-admin__btn--primary" onClick={() => setShowAdd(true)}>
              {F.addValue}
            </button>
          </div>
        )}
      </div>

      <WarningsBanner items={banners} />

      {loading && !detail && (
        <div role="status" aria-live="polite">
          {F.loading}
        </div>
      )}

      {error && (
        <div role="alert">
          <p>{F.errorTitle}</p>
          <p>{error.message}</p>
        </div>
      )}

      {detail && detail.list.values.length === 0 && <p>{F.empty}</p>}

      {detail && detail.list.values.length > 0 && (
        <table>
          <caption>{detail.list.displayName}</caption>
          <thead>
            <tr>
              <th scope="col">{F.code}</th>
              <th scope="col">{F.canonicalLabel}</th>
              {showColorColumn && <th scope="col">{F.colorHex}</th>}
              <th scope="col">{F.active}</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {detail.list.values.map((value) => (
              <tr key={value.key}>
                {editingKey === value.key ? (
                  <td colSpan={columnCount}>
                    <form
                      className="eg-admin__form"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void saveValue(referenceValueFromDraft(listCode, editDraft, value));
                      }}
                    >
                      <ReferenceValueForm
                        idPrefix={`eg-admin-edit-${value.key}`}
                        draft={editDraft}
                        onChange={(patch) => setEditDraft((current) => ({ ...current, ...patch }))}
                      />
                      <div className="eg-admin__actions">
                        <button type="submit" className="eg-admin__btn--primary" disabled={saving}>
                          {F.save}
                        </button>
                        <button type="button" disabled={saving} onClick={() => setEditingKey(null)}>
                          {F.cancel}
                        </button>
                      </div>
                    </form>
                  </td>
                ) : (
                  <>
                    <td>{value.code}</td>
                    <td>{value.canonicalLabel}</td>
                    {showColorColumn && (
                      <td>
                        {value.colorHex ? (
                          <span className="eg-admin__color-cell">
                            {/* The swatch background is the value's own stored hex - runtime data,
                                not a literal - the same treatment as BrandingPanel's swatches and
                                D-247's rimmed chips/dots. */}
                            <span className="eg-admin__swatch" style={{ backgroundColor: value.colorHex }} aria-hidden="true" />
                            <span className="eg-admin__hex">{value.colorHex}</span>
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    )}
                    <td>{value.isActive ? F.active : "—"}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => {
                          setEditDraft(draftFromReferenceValue(value));
                          setEditingKey(value.key);
                        }}
                      >
                        {F.edit}
                      </button>
                      {value.isActive ? (
                        <button type="button" className="eg-admin__btn--danger" onClick={() => void startRetire(value)}>
                          {F.retire}
                        </button>
                      ) : (
                        <button type="button" onClick={() => void reactivate(value)}>
                          {F.reactivate}
                        </button>
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {retiringKey && (
        <div className="eg-admin-reference-data__retirement-impact">
          <h3>{F.retirementImpactHeading}</h3>
          {assessingImpact && <p>{F.retirementImpactLoading}</p>}
          {retirementImpact && (
            <p>
              {retirementImpact.referencingRowCount} {F.retirementImpactRows}
            </p>
          )}
          <ConfirmDialog
            open={!assessingImpact}
            title={F.retireConfirmTitle}
            body={F.retireConfirmBody}
            confirmLabel={F.retireConfirmConfirm}
            cancelLabel={F.retireConfirmCancel}
            onConfirm={() => {
              const value = detail?.list.values.find((candidate) => candidate.key === retiringKey);
              if (value) void confirmRetire(value);
            }}
            onCancel={() => setRetiringKey(null)}
          />
        </div>
      )}

      {showAdd && (
        <form
          className="eg-admin__form"
          onSubmit={(event) => {
            event.preventDefault();
            void saveValue(referenceValueFromDraft(listCode, addDraft, null));
          }}
        >
          <ReferenceValueForm
            idPrefix="eg-admin-add"
            draft={addDraft}
            onChange={(patch) => setAddDraft((current) => ({ ...current, ...patch }))}
          />
          <div className="eg-admin__actions">
            <button type="submit" className="eg-admin__btn--primary" disabled={saving}>
              {saving ? F.saving : F.addValue}
            </button>
            <button type="button" disabled={saving} onClick={() => setShowAdd(false)}>
              {F.cancel}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
